import React, { useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { CoreConfigField } from '../../../../interfaces/examiner/CoreConfig'
import {
    formatDateValue,
    formatDayMonthValue,
    parseDateValue,
    parseDayMonthValue,
} from './helpers'
import { ConfigurationsClasses } from './styles'

interface Props {
    field: CoreConfigField
    onChange: (key: string, value: string) => void
    onAction: (key: string) => void
    actionPending: boolean
    classes: ConfigurationsClasses
}

export default function ConfigFieldRow({ field, onChange, onAction, actionPending, classes }: Props) {
    const [confirmOpen, setConfirmOpen] = useState(false)

    return (
      <div className={classes.fieldRow}>
          <div className={classes.fieldLabel}>
              {field.label}
              {field.required && (
                <span className={classes.requiredMark} aria-hidden="true">*</span>
              )}
          </div>
          <div className={classes.fieldControl}>
              {field.type === 'boolean' ? (
                <FormControlLabel
                  control={
                      <Switch
                        color="primary"
                        checked={field.value === 'true'}
                        onChange={(e) =>
                          onChange(field.key, e.target.checked ? 'true' : 'false')
                        }
                      />
                  }
                  label={field.value === 'true' ? 'Yes' : 'No'}
                />
              ) : field.type === 'number' ? (
                <TextField
                  type="number"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  inputProps={{ min: field.required ? 1 : 0, step: 1 }}
                  value={field.value}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  aria-label={field.label}
                />
              ) : field.type === 'readonly' ? (
                <Typography component="div" className={classes.readonlyValue}>
                    {field.value && field.value.trim() ? field.value : 'no data'}
                </Typography>
              ) : field.type === 'action' ? (
                <>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={actionPending}
                      onClick={() => setConfirmOpen(true)}
                    >
                        {actionPending ? 'Working...' : (field.buttonLabel || field.label)}
                    </Button>
                    <Dialog
                      open={confirmOpen}
                      onClose={() => {
                          if (!actionPending) setConfirmOpen(false)
                      }}
                      aria-labelledby={`config-action-confirm-${field.key}`}
                    >
                        <DialogTitle id={`config-action-confirm-${field.key}`}>
                            {field.label}
                        </DialogTitle>
                        <DialogContent>
                            <Typography>
                                {field.confirmMessage || field.description || 'Are you sure?'}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setConfirmOpen(false)} disabled={actionPending}>
                                No
                            </Button>
                            <Button
                              color="secondary"
                              variant="contained"
                              disabled={actionPending}
                              onClick={() => {
                                  setConfirmOpen(false)
                                  onAction(field.key)
                              }}
                            >
                                Yes
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
              ) : field.type === 'day_month' ? (
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      disableToolbar
                      variant="inline"
                      format="dd/MM"
                      views={['month', 'date']}
                      openTo="month"
                      margin="dense"
                      required
                      className={classes.requiredPicker}
                      label="Select day and month"
                      value={parseDayMonthValue(field.value)}
                      initialFocusedDate={new Date()}
                      onChange={(date) => onChange(field.key, formatDayMonthValue(date))}
                      KeyboardButtonProps={{ 'aria-label': field.label }}
                    />
                </MuiPickersUtilsProvider>
              ) : (
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      disableToolbar
                      variant="inline"
                      format="dd/MM/yyyy"
                      margin="dense"
                      label="Select date"
                      value={parseDateValue(field.value)}
                      initialFocusedDate={new Date()}
                      onChange={(date) => onChange(field.key, formatDateValue(date))}
                      KeyboardButtonProps={{ 'aria-label': field.label }}
                    />
                </MuiPickersUtilsProvider>
              )}
              {field.description && (
                <Typography component="span" className={classes.fieldComment}>
                    {field.description}
                </Typography>
              )}
          </div>
      </div>
    )
}
